import { Form, useFormState } from "react-final-form";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { createListCollection } from "@ark-ui/react";
import classnames from "classnames";
import { Eye } from "lucide-react";

import type {
  ObservationsMapWidgetConfiguration,
  ParameterTimeSeriesConfiguration,
  Widget,
} from "@/lib/api";
import {
  mergeObservationsMapTimeSeriesInitialValues,
  mergeObservationsMapDisplayGroupInitialValues,
  mergeObservationsMapStationFilterInitialValues,
} from "@/lib/api/observations-map-widget-configuration.utils";
import { ObservationsMapBaseLayerStyle } from "@/lib/mapbox/base-layer-styles";
import { mapEditorSaveAction } from "@/features/widget-forms/dialogs/map-editor-save-action";
import { WidgetDialogProps } from "@/features/widget-forms/dialogs/widget-dialog-types";
import { canonicalizeWidgetUpdatePayload } from "@/features/widget-forms/dialogs/is-unchanged-widget-update";
import Dialog from "@/components/dialog/dialog";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import { BaseLayerStylePanel } from "@/features/widget-forms/forms/map-widget-editor/base-layer-style-panel";
import { InitialViewportPanel } from "@/features/widget-forms/forms/map-widget-editor/initial-viewport-panel";
import {
  MapWidgetEditableTitle,
  type MapWidgetEditableTitleStrings,
} from "@/features/widget-forms/forms/map-widget-editor/map-widget-editable-title";
import { MapWidgetEditorPreviewColumn } from "@/features/widget-forms/forms/map-widget-editor/map-widget-editor-preview-column";
import { MapWidgetDndProvider } from "@/features/widget-forms/forms/map-widget-editor/map-widget-dnd-provider";
import * as MapWidgetEditor from "@/features/widget-forms/forms/map-widget-editor/map-widget-editor";
import {
  MapWidgetTabRail,
  type MapWidgetTabRailItem,
} from "@/features/widget-forms/forms/map-widget-editor/map-widget-tab-rail";
import PreviewModal from "@/features/widget-forms/forms/weather-model-map/modals/preview-modal";
import { TimeSeriesConfiguratorModal } from "@/features/widget-forms/forms/weather-model-map/modals/time-series-configurator-modal";
import {
  createEmptyTimeSeriesConfiguratorDraft,
  toTimeSeriesConfiguratorDraft,
} from "@/features/widget-forms/forms/weather-model-map/time-series-configurator.utils";
import { useMapPanelTabLayout } from "@/features/widget-forms/forms/map-widget-editor/use-map-panel-tab-layout";
import { WidgetContextHost } from "@/features/shared/widget-context/widget-context-host";
import { useAdminDashboard } from "@/features/admin/hooks";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import Icon from "@/components/icon/icon";
import { useExitGuard } from "@/features/admin/hooks/use-exit-guard";
import { initialViewportPresets } from "@/features/widget-forms/shared/initial-viewport-presets";
import styles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";
import { useTranslation } from "@/lib/i18n";

import {
  ObservationsMapPreviewContent,
  ObservationsMapPreviewToolbar,
} from "./observations-map-preview";
import {
  TimeSeriesChartPreviewBody,
  TimeSeriesChartPreviewProvider,
  TimeSeriesChartPreviewToolbar,
} from "./observations-map-time-series-chart-preview";
import { ObservationsMapParameterLayersPanel } from "./observations-map-parameter-layers-panel";
import { ObservationsMapStaticOverlaysPanel } from "./observations-map-static-overlays-panel";
import { ObservationsMapTimeSeriesTab } from "./observations-map-time-series-tab";
import { ObservationsMapMarkerDesignsTab } from "./observations-map-marker-designs-tab";
import { useObservationsMapForm } from "./use-observations-map-form";
import {
  ObservationsMapTimeSeriesConfiguratorProvider,
  type ObservationsMapTimeSeriesConfiguratorController,
} from "./observations-map-time-series-configurator-context";

enum ObservationsMapTabs {
  MapLayers = "map-layers",
  MarkerDesigns = "marker-designs",
  TimeSeries = "time-series",
  InitialViewport = "initial-viewport",
  BaseLayerStyle = "base-layer-style",
}

function mergeObservationsMapInitialValues(initialValues: Partial<Widget>) {
  const config = initialValues?.configuration;
  if (!config || config.type !== "observations_map") return initialValues;
  let configuration: ObservationsMapWidgetConfiguration = {
    ...(config as ObservationsMapWidgetConfiguration),
  };
  let changed = false;
  if (!configuration.initial_viewport_bounds) {
    configuration = {
      ...configuration,
      initial_viewport_bounds: initialViewportPresets.nl,
    };
    changed = true;
  }
  const style = configuration.base_layer_style;
  if (typeof style !== "string" || !style.trim()) {
    configuration = {
      ...configuration,
      base_layer_style: ObservationsMapBaseLayerStyle.Monochrome,
    };
    changed = true;
  }
  if (!Array.isArray(configuration.parameter_map_layer_configurations)) {
    configuration = {
      ...configuration,
      parameter_map_layer_configurations: [],
    };
    changed = true;
  }
  if (!Array.isArray(configuration.static_map_overlay_configurations)) {
    configuration = {
      ...configuration,
      static_map_overlay_configurations: [],
    };
    changed = true;
  }
  const layerConfigs = configuration.parameter_map_layer_configurations;
  if (layerConfigs?.some((row) => typeof row.enabled_by_default !== "boolean")) {
    configuration = {
      ...configuration,
      parameter_map_layer_configurations: layerConfigs.map((row) => ({
        ...row,
        enabled_by_default: row.enabled_by_default ?? false,
      })),
    };
    changed = true;
  }
  if (!changed) {
    return mergeObservationsMapStationFilterInitialValues(
      mergeObservationsMapDisplayGroupInitialValues(
        mergeObservationsMapTimeSeriesInitialValues(initialValues),
      ),
    );
  }
  return mergeObservationsMapStationFilterInitialValues(
    mergeObservationsMapDisplayGroupInitialValues(
      mergeObservationsMapTimeSeriesInitialValues({
        ...initialValues,
        configuration,
      }),
    ),
  );
}

export function ObservationsMapWidgetDialog({
  initialValues,
  open,
  onOpenChange,
  onSubmit,
  onReset,
  isCreate = false,
}: WidgetDialogProps) {
  const { t } = useTranslation();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const hasChangesRef = useRef(false);

  const handleFormOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && hasChangesRef.current) {
      setConfirmationOpen(true);
    } else {
      onOpenChange(nextOpen);
    }
  };

  const handleConfirmClose = () => {
    onOpenChange(false);
    setConfirmationOpen(false);
    onReset();
  };

  const handleCancelClose = () => {
    setConfirmationOpen(false);
  };

  const mergedInitialValues = useMemo(() => {
    const merged = mergeObservationsMapInitialValues(
      initialValues as Widget,
    ) as Partial<Widget>;
    // CreateWidgetDialog prefills Widget.title with "Naamloos". The header is
    // a required autofocused field; keep it empty until the user types a name.
    if (
      !initialValues.id &&
      merged.title?.trim() === t("admin.widgets.fields.name.initial_value")
    ) {
      return { ...merged, title: "" };
    }
    return merged;
  }, [initialValues, t]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleFormOpenChange} onClose={onReset}>
        <Dialog.Content className={classnames(dialogVariants.fullscreen, styles.dialog)}>
          <Form
            initialValues={mergedInitialValues}
            onSubmit={(values) =>
              onSubmit(canonicalizeWidgetUpdatePayload(values as Widget))
            }
          >
            {() => (
              <ObservationsMapEditorForm
                isCreate={isCreate}
                onClose={() => {
                  if (hasChangesRef.current) {
                    setConfirmationOpen(true);
                  } else {
                    onOpenChange(false);
                    onReset();
                  }
                }}
                hasChangesRef={hasChangesRef}
              />
            )}
          </Form>
        </Dialog.Content>
      </Dialog>
      <ConfirmationDialog
        open={confirmationOpen}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}

function ConfirmationDialog({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: React.DispatchWithoutAction;
  onCancel: React.DispatchWithoutAction;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open}>
      <Dialog.Content
        className={classnames(dialogVariants.small, styles.dialog, styles.confirmation)}
      >
        <h1>{t("admin.widgets.fields.map_editor.confirmation.title")}</h1>
        <span>{t("admin.widgets.fields.map_editor.confirmation.text")}</span>
        <div className={styles.confirmationButtons}>
          <Button type="button" variant="solid" onClick={onCancel}>
            {t("admin.widgets.fields.map_editor.confirmation.cancel")}
          </Button>
          <Button type="button" variant="primary" onClick={onConfirm}>
            {t("admin.widgets.fields.map_editor.confirmation.confirm")}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

function ObservationsMapEditorForm({
  isCreate,
  onClose,
  hasChangesRef,
}: {
  isCreate: boolean;
  onClose: () => void;
  hasChangesRef: React.MutableRefObject<boolean>;
}) {
  const { t } = useTranslation();
  const { submitting } = useFormState({ subscription: { submitting: true } });
  const dashboard = useAdminDashboard(false);
  const {
    hasChanges,
    dirtyBaselineReady,
    form,
    title,
    configuration,
    initialViewportBounds,
    setInitialViewportBounds,
    updateInitialViewportBoundsField,
    baseLayerStyle,
    setBaseLayerStyle,
    parameters,
    parametersLoading,
    unitFamilies,
    timeSeriesConfigurations,
    addTimeSeriesConfig,
    updateTimeSeriesConfig,
  } = useObservationsMapForm();
  hasChangesRef.current = hasChanges;

  const {
    horizontal: horizontalTabs,
    orientation: tabRailOrientation,
    variant: horizontalTabVariant,
  } = useMapPanelTabLayout();

  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const [configuratorIndex, setConfiguratorIndex] = useState<number | null>(null);

  const closeConfigurator = useCallback(() => {
    setConfiguratorOpen(false);
    setConfiguratorIndex(null);
  }, []);

  const configuratorController: ObservationsMapTimeSeriesConfiguratorController = useMemo(
    () => ({
      openFor: (index) => {
        setConfiguratorIndex(index);
        setConfiguratorOpen(true);
      },
    }),
    [],
  );

  const handleConfiguratorDone = useCallback(
    (config: ParameterTimeSeriesConfiguration) => {
      if (configuratorIndex === null) {
        addTimeSeriesConfig(config);
      } else {
        updateTimeSeriesConfig(configuratorIndex, config);
      }
      closeConfigurator();
    },
    [configuratorIndex, addTimeSeriesConfig, updateTimeSeriesConfig, closeConfigurator],
  );

  const configuratorInitialConfig = useMemo(
    () =>
      configuratorIndex !== null && timeSeriesConfigurations[configuratorIndex]
        ? toTimeSeriesConfiguratorDraft(timeSeriesConfigurations[configuratorIndex])
        : createEmptyTimeSeriesConfiguratorDraft(),
    [configuratorIndex, timeSeriesConfigurations],
  );

  const tabItems: MapWidgetTabRailItem[] = useMemo(
    () => [
      {
        label: t("admin.widgets.fields.observations_map.tabs.map_layers"),
        value: ObservationsMapTabs.MapLayers,
        icon: "layers",
      },
      {
        label: t("admin.widgets.fields.observations_map.tabs.time_series"),
        value: ObservationsMapTabs.TimeSeries,
        icon: "chart-line",
      },
      {
        label: t("admin.widgets.fields.observations_map.tabs.marker_designs"),
        value: ObservationsMapTabs.MarkerDesigns,
        icon: "map-pin",
      },
      {
        label: t("admin.widgets.fields.observations_map.tabs.initial_viewport"),
        value: ObservationsMapTabs.InitialViewport,
        icon: "map",
      },
      {
        label: t("admin.widgets.fields.observations_map.tabs.base_layer_style"),
        value: ObservationsMapTabs.BaseLayerStyle,
        icon: "palette",
      },
    ],
    [t],
  );

  const [activeTab, setActiveTab] = useState(ObservationsMapTabs.MapLayers);

  const [previewDialogVisible, setPreviewDialogVisible] = useState(false);

  const initialViewportStrings = useMemo(
    () => ({
      title: t("admin.widgets.fields.map_editor.initial_viewport.title"),
      subtitle: t("admin.widgets.fields.map_editor.initial_viewport.subtitle"),
      manualTitle: t("admin.widgets.fields.map_editor.initial_viewport.manual_title"),
      northLatitude: t("admin.widgets.fields.map_editor.initial_viewport.north_latitude"),
      westLongitude: t("admin.widgets.fields.map_editor.initial_viewport.west_longitude"),
      eastLongitude: t("admin.widgets.fields.map_editor.initial_viewport.east_longitude"),
      southLatitude: t("admin.widgets.fields.map_editor.initial_viewport.south_latitude"),
      presetsTitle: t("admin.widgets.fields.map_editor.initial_viewport.presets_title"),
      presetNl: t("admin.widgets.fields.map_editor.initial_viewport.preset_nl"),
      presetNlDescription: t(
        "admin.widgets.fields.map_editor.initial_viewport.preset_nl_description",
      ),
      presetBes: t("admin.widgets.fields.map_editor.initial_viewport.preset_bes"),
      presetBesDescription: t(
        "admin.widgets.fields.map_editor.initial_viewport.preset_bes_description",
      ),
      selectedStatusText: t("admin.widgets.fields.observations_map.map_layers.selected"),
    }),
    [t],
  );

  const baseLayerStrings = useMemo(
    () => ({
      title: t("admin.widgets.fields.map_editor.base_layer_style.title"),
      subtitle: t("admin.widgets.fields.map_editor.base_layer_style.subtitle"),
      styleLabel: t("admin.widgets.fields.map_editor.base_layer_style.style_label"),
    }),
    [t],
  );

  const baseLayerStyleCollection = useMemo(
    () =>
      createListCollection<{ label: string; value: string }>({
        items: [
          {
            label: t("admin.widgets.fields.model_map.base_layer_style.option_monochrome"),
            value: ObservationsMapBaseLayerStyle.Monochrome,
          },
          {
            label: t("admin.widgets.fields.model_map.base_layer_style.option_full_color"),
            value: ObservationsMapBaseLayerStyle.FullColor,
          },
        ],
      }),
    [t],
  );

  const previewColumnStrings = useMemo(
    () => ({
      previewTitle: t("admin.widgets.fields.map_editor.preview.title"),
      previewButton: t("admin.widgets.fields.map_editor.preview.button"),
    }),
    [t],
  );

  const titleStrings: MapWidgetEditableTitleStrings = useMemo(
    () => ({
      untitled: t("admin.widgets.fields.model_map.general.untitled"),
      nameLabel: t("admin.widgets.fields.name.label"),
      confirmTitleAria: t("admin.widgets.fields.model_map.general.confirm_title"),
      cancelEditingAria: t("admin.widgets.fields.model_map.general.cancel_editing"),
      editTitleAria: t("admin.widgets.fields.model_map.general.edit_title"),
    }),
    [t],
  );

  const handleTitleChange = useCallback(
    (next: string) => {
      form.change("title", next);
    },
    [form],
  );

  const preventNativeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const boundsInteractionMode =
    activeTab === ObservationsMapTabs.InitialViewport
      ? ("editable" as const)
      : ("none" as const);

  const settingsPanel =
    activeTab === ObservationsMapTabs.MapLayers ? (
      <>
        <ObservationsMapParameterLayersPanel />
        <ObservationsMapStaticOverlaysPanel />
      </>
    ) : activeTab === ObservationsMapTabs.MarkerDesigns ? (
      <ObservationsMapMarkerDesignsTab />
    ) : activeTab === ObservationsMapTabs.TimeSeries ? (
      <ObservationsMapTimeSeriesTab />
    ) : activeTab === ObservationsMapTabs.InitialViewport ? (
      <InitialViewportPanel
        strings={initialViewportStrings}
        bounds={initialViewportBounds}
        baseLayerStyle={baseLayerStyle}
        onBoundsChange={setInitialViewportBounds}
        onCoordChange={updateInitialViewportBoundsField}
      />
    ) : (
      <BaseLayerStylePanel
        strings={baseLayerStrings}
        collection={baseLayerStyleCollection}
        value={baseLayerStyle}
        onValueChange={setBaseLayerStyle}
        fieldName="configuration.base_layer_style"
        previewBounds={initialViewportBounds}
        baseLayerStyle={baseLayerStyle}
      />
    );

  // The editable bounds map stays on the Initial Viewport tab; every other tab
  // shows the live widget preview driven by the current draft config.
  const showLivePreview = activeTab !== ObservationsMapTabs.InitialViewport;

  const previewColumn = (
    <MapWidgetEditorPreviewColumn
      bounds={initialViewportBounds}
      baseLayerStyle={baseLayerStyle}
      onBoundsChange={setInitialViewportBounds}
      boundsInteractionMode={boundsInteractionMode}
      fitBoundsPadding={showLivePreview ? 0 : 40}
      onExpand={() => setPreviewDialogVisible(true)}
      strings={previewColumnStrings}
      // The toolbar and the live preview share the surrounding host below, so the
      // title-bar controls (time range, refresh) drive the previewed map.
      toolbar={showLivePreview ? <ObservationsMapPreviewToolbar /> : undefined}
      content={
        showLivePreview ? (
          <ObservationsMapPreviewContent configuration={configuration} />
        ) : undefined
      }
    />
  );

  // Only the live preview needs a runtime host; the Initial Viewport tab shows a
  // plain editable bounds map with no time/refresh controls.
  const hostedPreviewColumn = showLivePreview ? (
    <WidgetContextHost dashboardId={dashboard?.id}>{previewColumn}</WidgetContextHost>
  ) : (
    previewColumn
  );

  const previewContent = hostedPreviewColumn;

  useExitGuard();

  return (
    <MapWidgetDndProvider>
      <ObservationsMapTimeSeriesConfiguratorProvider value={configuratorController}>
        <MapWidgetEditor.Root
          onSubmit={preventNativeSubmit}
          className={horizontalTabs ? styles.panelGridHorizontalTabs : undefined}
        >
          <MapWidgetEditor.Header>
            <MapWidgetEditableTitle
              value={title}
              onChange={handleTitleChange}
              strings={titleStrings}
              variant="auto"
            />
            {/* <div className={styles.subtitle}>
              {t("admin.widgets.fields.observations_map.subtitle")}
            </div> */}
            <Button
              type="button"
              variant="card"
              className={styles.previewButton}
              onClick={() => setPreviewDialogVisible(true)}
            >
              <Stack gap={8} alignItems="center">
                <Eye size={16} />
                <span>{previewColumnStrings.previewButton}</span>
              </Stack>
            </Button>
            <WidgetContextHost dashboardId={dashboard?.id}>
              <PreviewModal
                open={previewDialogVisible}
                onOpenChange={setPreviewDialogVisible}
                title={title}
                toolbar={<ObservationsMapPreviewToolbar />}
                previewNode={
                  <ObservationsMapPreviewContent configuration={configuration} />
                }
              />
            </WidgetContextHost>
          </MapWidgetEditor.Header>
          <MapWidgetEditor.Nav>
            <MapWidgetTabRail
              items={tabItems}
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as ObservationsMapTabs)}
              orientation={tabRailOrientation}
              variant={horizontalTabVariant}
            />
          </MapWidgetEditor.Nav>
          <MapWidgetEditor.Settings>{settingsPanel}</MapWidgetEditor.Settings>
          <MapWidgetEditor.Preview>{previewContent}</MapWidgetEditor.Preview>
          <MapWidgetEditor.Footer>
            <Stack justifyContent="flex-end" gap={8}>
              <Button type="button" variant="ghost" onClick={onClose}>
                {t("general.cancel")}
              </Button>
              <Button
                type="button"
                variant="solid"
                onClick={() => {
                  if (
                    mapEditorSaveAction({ isCreate, dirtyBaselineReady, hasChanges }) ===
                    "close"
                  ) {
                    onClose();
                    return;
                  }
                  form.submit();
                }}
                loading={submitting || !title.trim()}
              >
                {t("general.save")}
                <Icon name="save" />
              </Button>
            </Stack>
          </MapWidgetEditor.Footer>
        </MapWidgetEditor.Root>
        <TimeSeriesConfiguratorModal
          open={configuratorOpen}
          onOpenChange={(nextOpen) =>
            nextOpen ? setConfiguratorOpen(true) : closeConfigurator()
          }
          initialConfig={configuratorInitialConfig}
          parameters={parameters ?? []}
          unitFamilies={unitFamilies}
          parametersLoading={parametersLoading}
          timeSeriesConfigurations={timeSeriesConfigurations}
          configuratorIndex={configuratorIndex}
          onDone={handleConfiguratorDone}
          onCancel={closeConfigurator}
          renderPreview={(liveConfig) => ({
            wrap: (node) => (
              <WidgetContextHost dashboardId={dashboard?.id}>
                <TimeSeriesChartPreviewProvider
                  config={liveConfig}
                  parameters={parameters ?? []}
                >
                  {node}
                </TimeSeriesChartPreviewProvider>
              </WidgetContextHost>
            ),
            toolbar: <TimeSeriesChartPreviewToolbar />,
            content: <TimeSeriesChartPreviewBody />,
          })}
        />
      </ObservationsMapTimeSeriesConfiguratorProvider>
    </MapWidgetDndProvider>
  );
}
